import type { Position } from '@/types'

/**
 * Display-time aggregation for share positions.
 *
 * DESIGN DECISION (2026-09-15, Option B): Supabase keeps SEPARATE rows per lot —
 * we do NOT merge on insert. Each assignment or direct "Add Position" writes its
 * own row. The POSITIONS TABLE on the website groups all open lots for the SAME
 * TICKER into a single displayed line and runs the business logic (blended cost,
 * adjusted cost) at render time. Expanding a line can still show the raw lots.
 *
 * Why: preserves individual lot history in the DB while showing the user the one
 * aggregated position they care about.
 */

export interface AggregatedPosition {
  /** The ticker these lots belong to. */
  ticker: string
  /** Total shares across all open lots. */
  quantity: number
  /** Quantity-weighted average cost per share, rounded to 2 decimals. */
  costBasis: number
  /** Adjusted cost = blended cost − (total linked closed-trade premiums / total shares), 2 dp. */
  adjustedCostBasis: number
  /** Sum of linked CLOSED-trade premiums (netPosition) across every lot in the group. */
  totalLinkedPremium: number
  /** Count of linked trades (any status) across every lot in the group. */
  linkedTradeCount: number
  /** Total cost = Σ(quantity × costBasis of each lot) = quantity × blended costBasis. */
  totalCost: number
  /** Earliest acquisition date across the lots (for the "Acquired" column / sorting). */
  acquisitionDate: string
  /** The underlying lots, FIFO order (oldest acquisition first). Used by the expand view + sell targeting. */
  lots: Position[]
  /**
   * Shares covered by OPEN covered calls for this ticker = Σ(qty × 100) over trades
   * where status === 'open' AND action === 'CC' AND ticker matches. Matched by TICKER
   * (linked OR unlinked — CCs are sometimes written before being linked).
   */
  coveredShares: number
  /**
   * CC-coverage state vs shares held:
   *  'none'    → coveredShares === 0 (no open CC)      → RED dot
   *  'partial' → 0 < coveredShares < quantity          → AMBER dot
   *  'full'    → coveredShares >= quantity             → GREEN dot
   */
  coverage: 'none' | 'partial' | 'full'
}

/** Trade shape we rely on for premium aggregation + CC-coverage. */
interface LinkedTradeLike {
  positionId?: string
  status?: string
  netPosition?: number
  ticker?: string
  action?: string
  qty?: number
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Earliest non-empty acquisition date among lots (ISO strings sort lexically). */
function earliestAcquisition(lots: Position[]): string {
  return lots
    .map((l) => (l as any).assignedDate || l.acquisitionDate || '')
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))[0] || ''
}

/**
 * Group open positions by ticker and compute the aggregated view-model for each.
 *
 * @param openPositions positions already filtered to status === 'open'
 * @param trades all trades (used to sum linked CLOSED-trade premiums per lot)
 * @returns one AggregatedPosition per distinct ticker
 */
export function aggregatePositionsByTicker(
  openPositions: Position[],
  trades: LinkedTradeLike[]
): AggregatedPosition[] {
  const byTicker = new Map<string, Position[]>()
  for (const p of openPositions) {
    const list = byTicker.get(p.ticker)
    if (list) list.push(p)
    else byTicker.set(p.ticker, [p])
  }

  const result: AggregatedPosition[] = []
  for (const [ticker, rawLots] of byTicker) {
    // FIFO order: oldest acquisition first (determines which lot a partial sell draws from).
    const lots = [...rawLots].sort((a, b) =>
      (((a as any).assignedDate || a.acquisitionDate || '') as string).localeCompare(
        ((b as any).assignedDate || b.acquisitionDate || '') as string
      )
    )

    const quantity = lots.reduce((s, l) => s + (l.quantity || 0), 0)
    const totalCost = lots.reduce((s, l) => s + (l.quantity || 0) * (l.costBasis || 0), 0)
    const costBasis = quantity > 0 ? round2(totalCost / quantity) : 0

    // Sum linked CLOSED-trade premiums across every lot in the group.
    const lotIds = new Set(lots.map((l) => l.id))
    const linked = trades.filter((t) => t.positionId && lotIds.has(t.positionId as string))
    const totalLinkedPremium = linked
      .filter((t) => t.status === 'closed')
      .reduce((s, t) => s + (t.netPosition || 0), 0)

    const adjustedCostBasis =
      quantity > 0 ? round2(costBasis - totalLinkedPremium / quantity) : costBasis

    // CC coverage: match OPEN CC trades by ticker (linked or unlinked). Each CC
    // contract (qty) covers 100 shares.
    const coveredShares = trades
      .filter((t) => t.status === 'open' && t.action === 'CC' && t.ticker === ticker)
      .reduce((s, t) => s + (Number(t.qty) || 0) * 100, 0)
    const coverage: AggregatedPosition['coverage'] =
      coveredShares <= 0 ? 'none' : coveredShares >= quantity ? 'full' : 'partial'

    result.push({
      ticker,
      quantity,
      costBasis,
      adjustedCostBasis,
      totalLinkedPremium: round2(totalLinkedPremium),
      linkedTradeCount: linked.length,
      totalCost: round2(totalCost),
      acquisitionDate: earliestAcquisition(lots),
      lots,
      coveredShares,
      coverage,
    })
  }

  return result
}
